import { useEffect, useRef, useState } from "react"
import Icon from "../../components/Icon"
import ConfirmModal from "../../components/ConfirmModal"
import { getApiErrorMessage } from "../../utils/errorMessage"
import { buildRequestFingerprint, clearStableRequestKey, getStableRequestKey } from "../../utils/requestKey"
import AutopayCompanyRow from "./AutopayCompanyRow"
import BillActionInput from "./BillActionInput"
import {
  buildPayBillConfirm,
  buildTurnOnConfirm,
  countWaitingCompanies,
  describeApprovalResult,
  describePaymentResult,
  getMaxBillPayment,
  getPayAmountError,
} from "./autopayApprovalDisplay"
import {
  MAX_OBJECTION_MESSAGE_LENGTH,
  buildObjectBillConfirm,
  describeObjectionSent,
  getObjectionMessageError,
} from "./billObjectionDisplay"

const PAY_STORAGE_PREFIX = "utbills:pay-bill:"

const CONFIRM_LOOK = {
  on: { icon: "shield-exclamation", tone: "amber", iconTone: "emerald" },
  pay: { icon: "wallet2", tone: "teal" },
  object: { icon: "flag-fill", tone: "rose" },
}

// Every company that bills this person, each with an auto-pay switch. A company
// is only paid from the wallet by itself once the person turns it on; until then
// its bills wait here. Each unpaid bill can be paid in full or in part, or its
// amount objected to while nothing is paid on it.
function AutopayCompaniesPanel({ companies = [], walletBalance = 0, focus = false, onSetApproval, onPayBill, onObjectBill }) {
  const waitingCount = countWaitingCompanies(companies)
  const [open, setOpen] = useState(waitingCount > 0 || focus)
  const [confirm, setConfirm] = useState(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)
  // What the person types in the open popup: an amount to pay, or why they object.
  const [input, setInput] = useState("")
  const sectionRef = useRef(null)

  // Opened from a dashboard notification: show the panel and bring it into view.
  useEffect(() => {
    if (!focus) return
    setOpen(true)
    sectionRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" })
  }, [focus])

  if (!companies.length) return null

  const saveApproval = async (company, approved) => {
    setBusy(true)
    setNotice(null)
    try {
      const result = await onSetApproval(company.providerId, approved)
      setNotice({ tone: "success", text: describeApprovalResult({ company, approved, payment: result?.payment }) })
      setConfirm(null)
    } catch (err) {
      setNotice({ tone: "error", text: getApiErrorMessage(err, "Could not save your choice. Please try again.") })
      setConfirm(null)
    } finally {
      setBusy(false)
    }
  }

  const payBill = async ({ company, bill, payNow }) => {
    const storageKey = `${PAY_STORAGE_PREFIX}${bill.invoiceId}`
    const requestKey = getStableRequestKey({
      storageKey,
      fingerprint: buildRequestFingerprint({ invoiceId: bill.invoiceId, payNow }),
      prefix: "utbill-pay",
    })
    setBusy(true)
    setNotice(null)
    try {
      const result = await onPayBill(bill.invoiceId, requestKey, payNow)
      clearStableRequestKey({ storageKey, requestKey })
      setNotice({ tone: "success", text: describePaymentResult({ company, result }) })
    } catch (err) {
      setNotice({ tone: "error", text: getApiErrorMessage(err, "Could not pay this bill. Please try again.") })
    } finally {
      setConfirm(null)
      setBusy(false)
    }
  }

  const handleToggle = (company, approved) => {
    // Turning a company off moves no money, so it saves at once. Turning it on
    // can pay bills, so the person sees exactly what will happen first.
    if (!approved) {
      saveApproval(company, false)
      return
    }
    setConfirm({ kind: "on", company, content: buildTurnOnConfirm(company, walletBalance) })
  }

  const sendObjection = async ({ company, bill, message }) => {
    setBusy(true)
    setNotice(null)
    try {
      await onObjectBill(bill.invoiceId, message.trim())
      setNotice({ tone: "success", text: describeObjectionSent(company) })
    } catch (err) {
      setNotice({ tone: "error", text: getApiErrorMessage(err, "Could not send your objection. Please try again.") })
    } finally {
      setConfirm(null)
      setBusy(false)
    }
  }

  // The amount box starts at the most the wallet can pay, so paying in full is
  // still one tap. The person can lower it to pay part of the bill.
  const handlePay = (company, bill) => {
    setInput(String(getMaxBillPayment(bill, walletBalance)))
    setConfirm({ kind: "pay", company, bill })
  }

  const handleObject = (company, bill) => {
    setInput("")
    setConfirm({ kind: "object", company, bill })
  }

  const inputError = !confirm
    ? ""
    : confirm.kind === "pay"
      ? getPayAmountError(input, getMaxBillPayment(confirm.bill, walletBalance))
      : confirm.kind === "object"
        ? getObjectionMessageError(input)
        : ""
  const content = !confirm
    ? null
    : confirm.kind === "pay"
      ? buildPayBillConfirm(confirm.bill, confirm.company, walletBalance, inputError ? 0 : Number(input))
      : confirm.kind === "object"
        ? buildObjectBillConfirm(confirm.bill, confirm.company)
        : confirm.content

  const onConfirm = () => {
    if (!confirm || inputError) return
    if (confirm.kind === "on") saveApproval(confirm.company, true)
    else if (confirm.kind === "object") sendObjection({ company: confirm.company, bill: confirm.bill, message: input })
    else payBill({ company: confirm.company, bill: confirm.bill, payNow: content.payNow })
  }

  return (
    <section
      ref={sectionRef}
      className="scroll-mt-24 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
      aria-label="Companies that bill you"
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="flex min-w-0 items-center gap-2">
          <Icon name="shield-check" size="1rem" className="shrink-0 text-teal-600" />
          <span className="truncate text-sm font-black text-slate-900 sm:text-base">Companies that bill you</span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {waitingCount > 0 && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
              {waitingCount} waiting
            </span>
          )}
          <Icon name={open ? "chevron-up" : "chevron-down"} size="0.9rem" className="text-slate-400" />
        </span>
      </button>

      {open && (
        <div className="border-t border-slate-100">
          <p className="px-4 pt-3 text-xs text-slate-500">
            Your wallet pays automatically only when Auto-Pay is ON. Enable it only for providers you know.
          </p>

          {notice && (
            <p
              role={notice.tone === "error" ? "alert" : "status"}
              className={`mx-4 mt-2 rounded-xl px-3 py-2 text-xs font-medium ${
                notice.tone === "error"
                  ? "border border-red-200 bg-red-50 text-red-700"
                  : "border border-emerald-200 bg-emerald-50 text-emerald-800"
              }`}
            >
              {notice.text}
            </p>
          )}

          <ul className="mt-2 divide-y divide-slate-100 border-t border-slate-100">
            {companies.map((company) => (
              <AutopayCompanyRow
                key={company.providerId}
                company={company}
                walletBalance={walletBalance}
                busy={busy}
                onToggle={handleToggle}
                onPay={(bill) => handlePay(company, bill)}
                onObject={(bill) => handleObject(company, bill)}
              />
            ))}
          </ul>
        </div>
      )}

      <ConfirmModal
        open={Boolean(confirm)}
        title={content?.title}
        message={content?.message}
        note={content?.note}
        details={content?.details || []}
        icon={CONFIRM_LOOK[confirm?.kind]?.icon}
        tone={CONFIRM_LOOK[confirm?.kind]?.tone}
        iconTone={CONFIRM_LOOK[confirm?.kind]?.iconTone || null}
        confirmLabel={content?.confirmLabel}
        confirmDisabled={Boolean(inputError)}
        busy={busy}
        onConfirm={onConfirm}
        onCancel={() => setConfirm(null)}
      >
        {confirm?.kind === "pay" && (
          <BillActionInput
            id="utbill-pay-amount"
            label="Amount to pay"
            hint="Pay all of it, or only part. The rest stays owed."
            type="amount"
            value={input}
            error={inputError}
            disabled={busy}
            onChange={setInput}
          />
        )}
        {confirm?.kind === "object" && (
          <BillActionInput
            id="utbill-objection-message"
            label="What is wrong with the amount?"
            hint="The company sees this message."
            type="message"
            maxLength={MAX_OBJECTION_MESSAGE_LENGTH}
            value={input}
            error={input ? inputError : ""}
            disabled={busy}
            onChange={setInput}
          />
        )}
      </ConfirmModal>
    </section>
  )
}

export default AutopayCompaniesPanel
